import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Button, message } from "antd";
import { Download, Eye, FileText, FileSpreadsheet, Image } from "lucide-react";
import { TInventory } from "~/types/inventory";

export type ExportType = "csv" | "pdf" | "photo-list";

interface ExportColumn {
  key: string;
  label: string;
  enabled: boolean;
}

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  exportType: ExportType;
  inventories: TInventory[];
  onExport: (columns: string[], exportType: ExportType, action: "view" | "download") => void;
}

const defaultColumns: ExportColumn[] = [
  { key: "inventoryRef", label: "Ref #", enabled: true },
  { key: "clientInternalRef", label: "Client Ref #", enabled: true },
  { key: "title", label: "Title", enabled: true },
  { key: "artistName", label: "Artist", enabled: true },
  { key: "medium", label: "Medium", enabled: true },
  { key: "dimensions", label: "Dimensions", enabled: true },
  { key: "packingType", label: "Packing Type", enabled: true },
  { key: "status", label: "Status", enabled: true },
  { key: "location", label: "Location", enabled: true },
  { key: "value", label: "Value", enabled: false },
  { key: "weight", label: "Weight", enabled: false },
  { key: "description", label: "Description", enabled: false },
  { key: "notes", label: "Notes", enabled: false },
  { key: "pieceCount", label: "Piece Count", enabled: false },
  { key: "packageCount", label: "Package Count", enabled: false },
];

const exportTypeOptions: { value: ExportType; label: string; icon: React.ReactNode }[] = [
  { value: "csv", label: "CSV", icon: <FileSpreadsheet size={16} /> },
  { value: "pdf", label: "PDF", icon: <FileText size={16} /> },
  { value: "photo-list", label: "Photo List", icon: <Image size={16} /> },
];

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  inventories,
  onExport,
}) => {
  const [columns, setColumns] = useState<ExportColumn[]>(defaultColumns);
  const [selectedTypes, setSelectedTypes] = useState<ExportType[]>(["csv"]);

  useEffect(() => {
    if (open) {
      setColumns(defaultColumns);
      setSelectedTypes(["csv"]);
    }
  }, [open]);

  const toggleColumn = (key: string) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, enabled: !col.enabled } : col))
    );
  };

  const selectAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, enabled: true })));
  };

  const deselectAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, enabled: false })));
  };

  const toggleExportType = (type: ExportType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleExport = (action: "view" | "download") => {
    const selectedColumns = columns.filter((col) => col.enabled).map((col) => col.key);

    if (selectedColumns.length === 0) {
      message.warning("Please select at least one column to export.");
      return;
    }

    if (selectedTypes.length === 0) {
      message.warning("Please select at least one export format.");
      return;
    }

    selectedTypes.forEach((type) => {
      onExport(selectedColumns, type, action);
    });
  };

  const selectedCount = columns.filter((col) => col.enabled).length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center gap-2">
          <FileText size={20} />
          <span className="font-bold uppercase tracking-wide">
            Export Inventory
          </span>
        </div>
      }
      width={600}
      className="export-modal"
    >
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 border border-[#e5e5e5] rounded-lg">
          <p className="text-sm font-medium text-[#1a1a2e]">
            {inventories.length} item{inventories.length !== 1 ? "s" : ""} will be exported
          </p>
        </div>

        {/* Export Format Selection */}
        <div>
          <span className="text-sm font-medium text-[#1a1a2e] block mb-2">
            Export format
          </span>
          <div className="flex gap-2">
            {exportTypeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => toggleExportType(opt.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-bold uppercase tracking-wide transition-all ${
                  selectedTypes.includes(opt.value)
                    ? "bg-[#1a1a2e] text-white border-[#1a1a2e]"
                    : "bg-white text-[#1a1a2e] border-[#e5e5e5] hover:bg-[#fafafa]"
                }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Column Selection */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[#1a1a2e]">
            Select columns ({selectedCount} selected)
          </span>
          <div className="flex gap-2">
            <Button size="small" onClick={selectAll} className="text-xs">
              Select All
            </Button>
            <Button size="small" onClick={deselectAll} className="text-xs">
              Deselect All
            </Button>
          </div>
        </div>

        <div className="border border-[#e5e5e5] rounded-lg max-h-96 overflow-y-auto p-4 bg-white">
          <div className="grid grid-cols-2 gap-3">
            {columns.map((column) => (
              <div key={column.key} className="flex items-center">
                <Checkbox
                  checked={column.enabled}
                  onChange={() => toggleColumn(column.key)}
                  className="text-sm"
                >
                  <span className="text-sm">{column.label}</span>
                </Checkbox>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#e5e5e5]">
          <Button onClick={onClose} className="border border-[#e5e5e5] rounded-lg">
            Cancel
          </Button>
          <Button
            onClick={() => handleExport("view")}
            disabled={selectedTypes.length === 0}
            className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] font-bold uppercase tracking-wide flex items-center gap-2"
          >
            <Eye size={16} />
            View in New Tab
          </Button>
          <Button
            onClick={() => handleExport("download")}
            disabled={selectedTypes.length === 0}
            type="primary"
            className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white font-bold uppercase tracking-wide flex items-center gap-2"
          >
            <Download size={16} />
            Download
          </Button>
        </div>
      </div>
    </Modal>
  );
};
